import React, { ReactNode, useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import Icon from '../../../../components/_base/Icon';
import {
  useMediaQuery,
  MediaQueryContextType,
} from '../../../../lib/context-providers/media-query-provider';
import './Carousel.scss';

interface ICarousel {
  children?: ReactNode[];
  isGeneralSessions?: boolean;
}

const countSliders = {
  mobLimit: 1,
  tableLimit: 2,
  laptopLimit: 2,
  desktopLimit: 3,
};

const Carousel = ({
  children = [],
  isGeneralSessions = true,
}: ICarousel): JSX.Element => {
  const mediaQuery = useMediaQuery() as MediaQueryContextType;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSliderReady, setIsSliderReady] = useState(false);

  const getSlidesValue = (): number => {
    if (mediaQuery?.isMobile) return countSliders.mobLimit;
    if (mediaQuery?.isTable) return countSliders.tableLimit;
    if (mediaQuery?.isLaptop) return countSliders.laptopLimit;
    if (mediaQuery?.isDesktop) return countSliders.desktopLimit;
    return countSliders.desktopLimit;
  };

  const perView = getSlidesValue();
  const scrollBy = perView;

  const remainder = children.length % perView;
  const placeholdersNeeded = remainder === 0 ? 0 : perView - remainder;
  const totalSlidesWithPlaceholders = children.length + placeholdersNeeded;
  const maxRange = Math.max(0, totalSlidesWithPlaceholders - perView);

  const [sliderRef, sliderInstance] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    created() {
      setIsSliderReady(true);
    },
    range: {
      min: 0,
      max: maxRange,
    },
    breakpoints: {
      '(max-width: 600px)': {
        slides: { perView: countSliders.mobLimit, spacing: 16, origin: 'center' },
      },
      '(min-width: 601px) and (max-width: 768px)': {
        slides: { perView: countSliders.tableLimit, spacing: 16, origin: 'auto' },
      },
      '(min-width: 769px) and (max-width: 1024px)': {
        slides: { perView: countSliders.laptopLimit, spacing: 16, origin: 'auto' },
      },
      '(min-width: 1025px)': {
        slides: { perView: countSliders.desktopLimit, spacing: 24, origin: 'auto' },
      },
    },
  });

  const slider = sliderInstance?.current;

  const placeholderSlides = Array.from({ length: placeholdersNeeded }, (_, i) => (
    <div
      key={`placeholder-${i}`}
      className="keen-slider__slide px-2 md:px-3 lg:px-4 box-border flex justify-center opacity-0"
      aria-hidden="true"
    />
  ));

  const hasEnoughSlidesToScroll = children.length > perView;

  const getArrowOpacityClass = (isDisabled: boolean) => {
    return !isGeneralSessions && !hasEnoughSlidesToScroll
      ? 'opacity-0'
      : isDisabled
        ? 'opacity-50'
        : 'opacity-100';
  };

  const renderButton = (direction: 'prev' | 'next') => {
    const isDisabled = (() => {
      if (direction === 'prev') return currentSlide <= 0;
      const nextIndex = currentSlide + scrollBy;
      return nextIndex > maxRange;
    })();

    const handleClick = () => {
      if (!slider || !isSliderReady) return;

      const directionValue = direction === 'prev' ? -1 : 1;
      const proposedIdx = currentSlide + directionValue * scrollBy;
      const clampedIdx = Math.max(0, Math.min(proposedIdx, maxRange));

      slider.moveToIdx(clampedIdx, true);
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        className={`carousel-button ${direction} ${getArrowOpacityClass(isDisabled)}`}
        aria-label={direction === 'prev' ? 'Previous' : 'Next'}
        disabled={isDisabled}
      >
        {direction === 'prev'
          ? <Icon.ArrowRight className="fill-primary" height="55px" width="55px" />
          : <Icon.ArrowLeft className="fill-primary" height="55px" width="55px" />}
      </button>
    );
  };

  return (
    <div className="carousel-container relative overflow-visible">
      {renderButton('prev')}

      <div ref={sliderRef} className="keen-slider">
        {children.map((child, i) => (
          <div
            className="keen-slider__slide px-2 md:px-3 lg:px-4 box-border flex justify-center"
            key={i}
          >
            {child}
          </div>
        ))}
        {placeholderSlides}
      </div>
      {renderButton('next')}
    </div>
  );
};

export { Carousel };
